import styled from '@emotion/native';
import type { ColorName } from '@emotion/react';
import { Col, Row } from 'components/base';
import {
  Icon,
  type IconName,
  type IconProps,
  Skeleton,
  Text,
  Visible,
} from 'design-system/base';
import { BASE_SIZE, BORDER_WIDTH, MARGIN, PADDING } from 'design-system/theme';
import { dateTimeWithoutYearFormatter, noOp } from 'helpers';
import type React from 'react';
import { useCallback } from 'react';
import { Pressable } from 'react-native';

interface ItemProps {
  id: string;
  title: string;
  subTitle: string;
  firstColumn?: number | Date | string;
  actionIcon?: IconName;
  statusIcons?: IconProps[];
  backgroundColor?: ColorName;
  small?: boolean;
  onPress?: (id: string) => void;
}

const Wrapper = styled(Row)({
  marginTop: MARGIN['2xs'],
  flex: 1,
});

interface ContentWrapperProps {
  small?: boolean;
  backgroundColor?: ColorName;
}

const ContentWrapper = styled(Row)<ContentWrapperProps>(
  ({ theme, small, backgroundColor = 'base-grey' }) => ({
    backgroundColor: theme.color[backgroundColor],
    paddingRight: PADDING.xs,
    alignItems: 'center',
    height: small ? BASE_SIZE[32] : BASE_SIZE[48],
    flex: 1,
  }),
);

const SubtitleWrapper = styled(Col)(({ theme }) => ({
  borderStyle: 'solid',
  borderRightColor: theme.color.divider,
  borderRightWidth: BORDER_WIDTH[1],
  alignItems: 'center',
  justifyContent: 'center',
  width: BASE_SIZE[96],
  height: BASE_SIZE[48],
}));

const TitleWrapper = styled.View({
  marginLeft: MARGIN.s,
  flex: 1,
});

const ActionWrapper = styled.View({
  marginLeft: MARGIN.s,
});

export const Item: React.FC<ItemProps> = ({
  id,
  title: title,
  subTitle: subtitle,
  firstColumn: time,
  statusIcons,
  actionIcon = 'chevron-right',
  onPress = noOp,
  small = false,
  backgroundColor,
}) => {
  const handlePress = useCallback(() => onPress(id), [onPress, id]);

  return (
    <Pressable onPress={handlePress}>
      <Wrapper>
        <Skeleton>
          <ContentWrapper small={small} backgroundColor={backgroundColor}>
            <SubtitleWrapper isVisible={!!time}>
              <Text variant="caption-small">
                {typeof time === 'string'
                  ? time
                  : dateTimeWithoutYearFormatter(time)}
              </Text>
            </SubtitleWrapper>
            <SubtitleWrapper>
              <Text variant="caption-small">{subtitle}</Text>
            </SubtitleWrapper>
            <TitleWrapper>
              <Text variant="body" numberOfLines={1}>
                {title}
              </Text>
            </TitleWrapper>

            <Visible if={statusIcons}>
              {statusIcons?.map((icon, i) => {
                return (
                  <Icon
                    key={`${icon.name}-${i}`}
                    name={icon.name}
                    color={icon.color}
                    size={BASE_SIZE[20]}
                  />
                );
              })}
            </Visible>
            <ActionWrapper>
              <Icon name={actionIcon} size={BASE_SIZE[20]} />
            </ActionWrapper>
          </ContentWrapper>
        </Skeleton>
      </Wrapper>
    </Pressable>
  );
};

interface ItemPlaceholderProps {
  small?: boolean;
}

export const ItemPlaceholder: React.FC<ItemPlaceholderProps> = ({ small }) => (
  <Item small={small} id="_" title="_" subTitle="_" />
);
